import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {RouterOutlet} from '@angular/router';
import {FuseFullscreenComponent} from '@fuse/components/fullscreen';
import {FuseLoadingBarComponent} from '@fuse/components/loading-bar';
import {FuseNavigationService, FuseVerticalNavigationComponent,} from '@fuse/components/navigation';
import {FuseMediaWatcherService} from '@fuse/services/media-watcher';
import {NavigationService} from 'app/core/navigation/navigation.service';
import {Navigation} from 'app/core/navigation/navigation.types';
import {LanguagesComponent} from 'app/layout/common/languages/languages.component';
import {UserComponent} from 'app/layout/common/user/user.component';
import {Subject, takeUntil} from 'rxjs';
import {UsersService} from 'app/services/users.service';
import Keycloak from "keycloak-js";
import {Role} from "app/modules/admin/users/users.types";
import {UserDTO} from "../../../../model/user/user-dto.model";
import {NgIf} from "@angular/common";
import {version} from "../../../../../../package.json";
import {NotificationsService} from "app/layout/common/notifications/notifications.service";
import {NotificationsComponent} from "../../../common/notifications/notifications.component";
import {EstablishmentBranding, EstablishmentParamService} from "app/services/establishment-param.service";

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        UserComponent,
        MatIconModule,
        MatButtonModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        RouterOutlet,
        NotificationsComponent,
        NgIf,
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    roles: Role[] = [];
    navigation: Navigation;
    version: string;
    branding: EstablishmentBranding;
    user: {email:string, name:string, role: number, roleName: string} = {
        email: '',
        name: '',
        role: 0,
        roleName: ''
    };
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _navigationService: NavigationService,
        private _usersService: UsersService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _keycloak: Keycloak,
        private _establishmentParamService: EstablishmentParamService
    ) {
        this.branding = this._establishmentParamService.branding;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.version = version;

        // Branding de l'établissement (nom + image) pour la navbar et l'onglet
        this._establishmentParamService.branding$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((branding: EstablishmentBranding) => {
                this.branding = branding;
            });
        this._establishmentParamService.load().subscribe();

        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        this._usersService.getMyProfile().subscribe({
            next: (userDto: UserDTO) => {
                this.user.name = userDto.firstName + ' ' + userDto.lastName;
                this.user.email = userDto.email;
                this.user.role = userDto.roleId;
                this.user.roleName = userDto.roleName || '';
            },
            error: (err) => console.error("Impossible de charger le profil", err)
        });

        // Subscribe to the user service
        // this._userService.user$
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe((user: User) => {
        //         this.user = user;
        //     });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
